import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './UserPreferences.module.scss';
import { ButtonSize, ButtonStyle, ButtonVariant } from '../../../design-system/elements/Button/Button.types';
import { Select, Toast } from '@ds';
import { Button } from '@ds/elements/Button/Button';
import { IToastr } from '@ds/elements/Toast/Toast.types';
import { useGetUser } from 'src/shared/queries/useGetUser';
import { useSaveUserPreferences } from 'src/shared/mutations/useSaveUserPreferences';
import { IOption } from '@ds/elements/Select/Select.types';
import ProfileImage from 'src/components/ProfileImage/ProfileImage';
import ArrowRight from '@icons/arrow-right.svg';
import { UserDateFormat } from 'src/utils/formatters/formatDate';

const dateFormatOptions: IOption[] = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
  { value: 'YYYY/MM/DD', label: 'YYYY/MM/DD' },
];

const UserPreferences: React.FC = () => {
  const { data: user, refetch: refetchUserDetails } = useGetUser();
  const [selectedOption, setSelectedOption] = useState<IOption>();
  const toastRef = useRef<IToastr>(null);
  const navigate = useNavigate();

  const { mutate: savePreferences } = useSaveUserPreferences();

  useEffect(() => {
    if (user?.userSettings.dateFormat) {
      const option = dateFormatOptions.find((o) => o.value === user.userSettings.dateFormat);
      setSelectedOption(option);
    }
  }, [user]);

  const handleDateFormatChange = async (option: any) => {
    setSelectedOption(option);
    savePreferences(
      { dateFormat: option.value },
      {
        onSuccess: () => {
          toastRef.current?.show({ message: 'Saved', severity: 'success' });
          UserDateFormat.set(option.value);
          refetchUserDetails();
        },
        onError: (error) => {
          console.error(error);
          toastRef.current?.show({
            message: 'There was an error saving your settings. Try again later.',
            severity: 'error',
          });
        },
      }
    );
  };

  const goToTwoFactorAuth = () => {
    navigate('/user-preferences/two-factor');
  };

  return (
    <div className={`${styles['user-preferences']}`}>
      <Toast ref={toastRef} />
      <div className={`flex flex-col md:flex-row justify-between mb-2 ${styles['user-preferences__header']}`}>
        <h2 className="mb-4">My Preferences</h2>

        <div className={`flex gap-3`}>
          <ProfileImage size={56} />
          <div className={`flex flex-col justify-around`}>
            <span className={`${styles['profile-name']}`}>{user?.fullName}</span>
            <span className={`${styles['profile-title']}`}>Principal</span>
          </div>
        </div>
      </div>
      <div className={`${styles['user-preferences__content']}`}>
        <div className="flex flex-col gap-2">
          <div className="p-2 w-full md:w-[300px]">
            <h4 className="mb-4">System Preferences</h4>
            <label className="mb-1">Date Format</label>
            <Select
              placeholder="Select date format"
              onChange={handleDateFormatChange}
              options={dateFormatOptions}
              selectedOption={selectedOption || null}
            />
          </div>
          <hr className="w-full" />

          <div className="p-2 w-full md:w-[300px]">
            <h4 className="mb-4">Security</h4>
            <Button
              size={ButtonSize.small}
              variant={ButtonVariant.outlined}
              styleType={ButtonStyle.secondary}
              onClick={goToTwoFactorAuth}
            >
              <span>Two-Factor Authentication</span>
              <img src={ArrowRight} className="w-4" />
            </Button>
          </div>
          <hr className="w-full" />
        </div>
      </div>
    </div>
  );
};

export default UserPreferences;
